import React, { PropsWithChildren, Suspense } from 'react';
import {
  Box,
  Center,
  Checkbox,
  HStack,
  Image,
  Link,
  LinkBox,
  LinkOverlay,
  SkeletonCircle,
  SkeletonText,
  Spacer,
  Td,
  Text,
  Tr,
} from '@cardboard-ui/react';
import { ItemMenu } from './ItemMenu';
import { t } from '@lingui/macro';
import { ItemProps, LoadedItemProps, UploadingItemProps } from './index';
import { useItem } from 'components/ItemHandle';
import { UploadIcon } from 'components/ItemHandle/components/UploadingHandle';
import { SignatureIcon } from 'apps/TenantApp/screens/ItemViewer/FileRenderer/PrepareSignatureFileRenderer/SignatureIcon';

export const TableItem = (props: ItemProps) => (
  <Suspense fallback={<TableItemSkeleton />}>
    <UnsuspendedTableItem {...props} />
  </Suspense>
);

const UnsuspendedTableItem = ({
  node,
  isLoading,
  loadingPercentage,
  isFailed,
  isVirus,
  parentConnectionId,
  uploadName,
  uploadError,
  ...props
}: ItemProps) => {
  if (isLoading || isFailed) {
    return (
      <UploadingTableItem
        isFailed={isFailed}
        isVirus={isVirus}
        loadingPercentage={loadingPercentage}
        uploadName={uploadName}
        uploadError={uploadError}
        {...props}
      />
    );
  } else {
    return (
      <TrueTableItem
        node={node}
        parentConnectionId={parentConnectionId}
        {...props}
      />
    );
  }
};

const UploadingTableItem = ({
  isFailed,
  isVirus,
  loadingPercentage,
  uploadName,
  uploadError,
  isSelected,
  onSelectChange,
}: UploadingItemProps) => {
  uploadError = uploadError || t`Upload failed`;
  return (
    <Tr>
      {/*Guaranteeing the data cell is necessary for table continuity with existing files, otherwise the table freaks out*/}
      {/*This is because the row widths are dynamically calculated based on content, and a mismatch is quite problematic.*/}
      <Td p={0} w={4}>
        {onSelectChange ? (
          <Checkbox isChecked={isSelected} onChange={onSelectChange} />
        ) : null}
      </Td>
      <Td w="full">
        <HStack data-qa="item">
          <Center w={ICON_WIDTH}>
            <UploadIcon
              loadingPercentage={loadingPercentage}
              isFailed={isFailed}
              isVirus={isVirus}
            />
          </Center>
          <Box
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            flex={1}
          >
            {uploadName}
          </Box>
        </HStack>
      </Td>
      <TdSecondary>{isFailed ? uploadError : t`Uploading`}</TdSecondary>
      <TdSecondary></TdSecondary>
      <Td></Td>
    </Tr>
  );
};

const TrueTableItem = ({
  node,
  parentConnectionId,
  isSelected,
  onSelectChange,
  dateColumn,
}: LoadedItemProps) => {
  if (!node) {
    throw new Error('Invalid node');
  }

  const {
    icon,
    linkProps,
    description,
    publisherName,
    thumbnailSrc,
    name,
    lastModifiedDate,
    publishDate,
    signatureEnvelope,
  } = useItem({
    item: node,
  });

  const displayedDate =
    dateColumn === 'lastModifiedAt' ? lastModifiedDate : publishDate;
  let thumbnail;

  if (thumbnailSrc) {
    thumbnail = (
      <Image
        borderRadius="md"
        w={ICON_WIDTH}
        src={thumbnailSrc}
        h={ICON_WIDTH}
        fit="cover"
      />
    );
  } else {
    const _icon = React.cloneElement(icon, {
      variant: 'Large',
    });
    thumbnail = (
      <Center w={ICON_WIDTH} color="blue.500">
        {_icon}
      </Center>
    );
  }

  return (
    <Tr>
      <Td p={0} w={4}>
        {onSelectChange && (
          <Checkbox isChecked={isSelected} onChange={onSelectChange} p={2} />
        )}
      </Td>
      <Td
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        maxWidth={0}
      >
        <LinkBox display="inline">
          <HStack data-qa="item" spacing={1}>
            <Box display="inline-block" verticalAlign="middle">
              {thumbnail}
            </Box>
            <LinkOverlay as={Link} {...linkProps} verticalAlign="middle">
              <HStack display="inline">
                <Text as="span">{name}</Text>
                <SignatureIcon status={signatureEnvelope?.status || null} />
              </HStack>
            </LinkOverlay>
          </HStack>
        </LinkBox>
      </Td>
      <TdSecondary>
        {/* Only Vault items have publisherName (at this point)  */}
        {publisherName || description}
      </TdSecondary>
      <TdSecondary>{displayedDate || t`Draft`}</TdSecondary>
      <Td isNumeric>
        <HStack spacing={2}>
          <Spacer />
          {node && (
            <ItemMenu
              node={node}
              mode="table-item"
              connectionId={parentConnectionId}
            />
          )}
        </HStack>
      </Td>
    </Tr>
  );
};

const TableItemSkeleton = () => {
  return (
    <Tr>
      <Td p={0} w={4}>
        <Checkbox isDisabled />
      </Td>
      <Td w="full">
        <HStack w="100%">
          <SkeletonCircle size="8" />
          <Box w="100%">
            <SkeletonText w="70% " noOfLines={1} />
          </Box>
        </HStack>
      </Td>
      <TdSecondary></TdSecondary>
      <TdSecondary></TdSecondary>
      <Td isNumeric></Td>
    </Tr>
  );
};
const ICON_WIDTH = '30px';
const TdSecondary: React.FC<PropsWithChildren> = (props) => (
  <Td
    fontSize="sm"
    opacity={0.6}
    overflow="hidden"
    textOverflow="ellipsis"
    whiteSpace="nowrap"
    {...props}
  />
);
